import React, { useState, useEffect } from 'react';
import { useMemo } from 'react';
import { Grid, Typography, Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Article, CheckCircle, Schedule, Psychology, Lightbulb } from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useTheme } from '../utils/ThemeContext';
import { useInteractiveTutorial } from '../utils/InteractiveTutorialContext';
import '../assets/css/futuristic-modern.css';

function Dashboard() {
  const token = useMemo(() => localStorage.getItem("authToken"), []);
  const { isDarkMode } = useTheme();
  const { loadTutorialStatus } = useInteractiveTutorial();

  const [selectedQueryIndex, setSelectedQueryIndex] = useState(0);
  const [selectedQuery, setSelectedQuery] = useState({});
  const [dashboardData, setDashboardData] = useState({
    queriesList: [],
    totalReadUnreadData: { read: 0, unread: 0 },
    cumulativeReadUnreadArticlesPerQueryPerMonthThisYearData: [],
    numberReadUnreadArticlesPerQueryPerYearData: [],
    numberOfArticlesPerQueryPerYearData: [],
    numberOfArticlesPerQueryPerMonthThisYearData: [],
    numberOfArticlesPerQuery: [],
    aiClassificationData: { yes: 0, no: 0, maybe: 0, notClassified: 0 },
    articlesWithInsights: 0,
  });

  const [dataFetched, setDataFetched] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const COLORS = ["#6c3082", "#468230"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Load tutorial status on mount
  useEffect(() => {
    const loadStatus = async () => {
      if (token) {
        await loadTutorialStatus();
      }
    };
    loadStatus();
  }, [token, loadTutorialStatus]);

  // Custom tooltip components for dark mode
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: isDarkMode ? 'var(--surface-2)' : '#fff',
          border: `1px solid ${isDarkMode ? 'var(--glass-border)' : '#ccc'}`,
          borderRadius: 'var(--radius-md)',
          padding: '12px',
          boxShadow: 'var(--card-shadow)',
          color: isDarkMode ? 'var(--text-primary)' : '#333'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: isDarkMode ? 'var(--surface-2)' : '#fff',
          border: `1px solid ${isDarkMode ? 'var(--glass-border)' : '#ccc'}`,
          borderRadius: 'var(--radius-md)',
          padding: '12px',
          boxShadow: 'var(--card-shadow)',
          color: isDarkMode ? 'var(--text-primary)' : '#333'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{payload[0].name}</p>
          <p style={{ margin: '4px 0', color: payload[0].color }}>
            Value: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  // Chart styling for dark mode
  // eslint-disable-next-line no-unused-vars
  const chartStyle = {
    color: isDarkMode ? 'var(--text-primary)' : '#333'
  };

  useEffect(() => {
    const getDashboardData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/get-dashboard-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setDashboardData((prevData) => ({
          ...prevData,
          queriesList: response.data.queries.map((query) => ({ query })), // Include "All Queries" at index 0
          totalReadUnreadData: {
            read: response.data.total_read_articles_number || [0],
            unread: response.data.total_unread_articles_number || [0],
          },
          cumulativeReadUnreadArticlesPerQueryPerMonthThisYearData: response.data.cumulative_read_articles_per_query_per_month_this_year.map(
            (queryData, queryIndex) =>
              queryData.map((value, monthIndex) => ({
                date: monthNames[monthIndex],
                read: value,
                unread: response.data.cumulative_unread_articles_per_query_per_month_this_year?.[queryIndex]?.[monthIndex] || 0,
              }))
          ),
          numberReadUnreadArticlesPerQueryPerYearData:
          response.data.number_of_read_articles_per_query_per_year?.map((_, queryIndex) =>
            response.data.available_years_per_query?.[queryIndex]?.map((year, yearIndex) => ({
              date: year,
              read: response.data.number_of_read_articles_per_query_per_year?.[queryIndex]?.[yearIndex] || 0,
              unread: response.data.number_of_unread_articles_per_query_per_year?.[queryIndex]?.[yearIndex] || 0,
            }))
          ) || [],
          numberOfArticlesPerQueryPerYearData:
          response.data.number_of_articles_per_query_per_year?.map((articles, queryIndex) =>
            response.data.available_years_per_query?.[queryIndex]?.map((year, yearIndex) => ({
              date: year,
              articles: articles?.[yearIndex] || 0,
            }))
          ) || [],
          numberOfArticlesPerQueryPerMonthThisYearData:
            response.data.articles_per_query_per_month_this_year?.map((queryData, queryIndex) =>
              queryData.map((value, monthIndex) => ({
                date: monthNames[monthIndex],
                articles: value || 0,
              }))
            ) || [],
          numberOfArticlesPerQuery:
            response.data.queries.map((query, index) => ({
              name: query,
              value: response.data.number_of_articles_per_query?.[index] || 0,
            })) || [],
          aiClassificationData: {
            yes: response.data.ai_classified_yes || [0],
            no: response.data.ai_classified_no || [0],
            maybe: response.data.ai_classified_maybe || [0],
            notClassified: response.data.ai_not_classified || [0],
          },
          articlesWithInsights: response.data.articles_with_insights || [0],
        }));
        
        setDataFetched(true);
        
        } catch (error) {
          console.error("Error fetching dashboard data:", error);
        }
    };

    getDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-refresh dashboard data every 30 seconds
  useEffect(() => {
    if (!token) return;

    const getDashboardData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/get-dashboard-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setDashboardData((prevData) => ({
          ...prevData,
          queriesList: response.data.queries.map((query) => ({ query })),
          totalReadUnreadData: {
            read: response.data.total_read_articles_number || [0],
            unread: response.data.total_unread_articles_number || [0],
          },
          cumulativeReadUnreadArticlesPerQueryPerMonthThisYearData: response.data.cumulative_read_articles_per_query_per_month_this_year.map(
            (queryData, queryIndex) =>
              queryData.map((value, monthIndex) => ({
                date: monthNames[monthIndex],
                read: value,
                unread: response.data.cumulative_unread_articles_per_query_per_month_this_year?.[queryIndex]?.[monthIndex] || 0,
              }))
          ),
          numberReadUnreadArticlesPerQueryPerYearData:
          response.data.number_of_read_articles_per_query_per_year?.map((_, queryIndex) =>
            response.data.available_years_per_query?.[queryIndex]?.map((year, yearIndex) => ({
              date: year,
              read: response.data.number_of_read_articles_per_query_per_year?.[queryIndex]?.[yearIndex] || 0,
              unread: response.data.number_of_unread_articles_per_query_per_year?.[queryIndex]?.[yearIndex] || 0,
            }))
          ) || [],
          numberOfArticlesPerQueryPerYearData:
          response.data.number_of_articles_per_query_per_year?.map((articles, queryIndex) =>
            response.data.available_years_per_query?.[queryIndex]?.map((year, yearIndex) => ({
              date: year,
              articles: articles?.[yearIndex] || 0,
            }))
          ) || [],
          numberOfArticlesPerQueryPerMonthThisYearData:
            response.data.articles_per_query_per_month_this_year?.map((queryData, queryIndex) =>
              queryData.map((value, monthIndex) => ({
                date: monthNames[monthIndex],
                articles: value || 0,
              }))
            ) || [],
          numberOfArticlesPerQuery:
            response.data.queries.map((query, index) => ({
              name: query,
              value: response.data.number_of_articles_per_query?.[index] || 0,
            })) || [],
          aiClassificationData: {
            yes: response.data.ai_classified_yes || [0],
            no: response.data.ai_classified_no || [0],
            maybe: response.data.ai_classified_maybe || [0],
            notClassified: response.data.ai_not_classified || [0],
          },
          articlesWithInsights: response.data.articles_with_insights || [0],
        }));
      } catch (error) {
        console.error("Error refreshing dashboard data:", error);
      }
    };

    // Auto-refresh disabled - caused jarring UX during active use
    // Dashboard data is static analytics, doesn't need constant refreshing
    // const dashboardRefreshInterval = setInterval(() => {
    //   if (!document.hidden) { // Only refresh when tab is visible
    //     getDashboardData();
    //   }
    // }, 30000); // 30 seconds
    // 
    // return () => clearInterval(dashboardRefreshInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Restore the original data filtering logic
  const filterDataByQueryIndex = useMemo(() => {
    if (!dataFetched || selectedQueryIndex >= dashboardData.queriesList.length) {
      return {
        query: "All Queries",
        totalReadUnreadData: { read: 0, unread: 0 },
        cumulativeReadUnreadArticlesPerQueryPerMonthThisYearData: [],
        numberReadUnreadArticlesPerQueryPerYearData: [],
        numberOfArticlesPerQueryPerYearData: [],
        numberOfArticlesPerQueryPerMonthThisYearData: [],
        numberOfArticlesPerQuery: [],
        aiClassificationData: { yes: 0, no: 0, maybe: 0, notClassified: 0 },
        articlesWithInsights: 0,
      };
    }
    
    // If selectedQueryIndex is 0, show "All Queries" data (backend index 0)
    // If selectedQueryIndex > 0, show individual query data (backend index = selectedQueryIndex)
    return {
      query: dashboardData.queriesList[selectedQueryIndex]?.query || "All Queries",
      totalReadUnreadData: {
        read: dashboardData.totalReadUnreadData.read?.[selectedQueryIndex],
        unread: dashboardData.totalReadUnreadData.unread?.[selectedQueryIndex],
      },
      cumulativeReadUnreadArticlesPerQueryPerMonthThisYearData:
      dashboardData.cumulativeReadUnreadArticlesPerQueryPerMonthThisYearData?.[selectedQueryIndex]?.map((monthData) => ({
        date: monthData.date,
        read: monthData.read,
        unread: monthData.unread,
      })) || [],

    numberReadUnreadArticlesPerQueryPerYearData:
      dashboardData.numberReadUnreadArticlesPerQueryPerYearData?.[selectedQueryIndex]?.map((yearData) => ({
        date: yearData.date,
        read: yearData.read,
        unread: yearData.unread,
      })) || [],

    numberOfArticlesPerQueryPerYearData:
      dashboardData.numberOfArticlesPerQueryPerYearData?.[selectedQueryIndex]?.map((yearData) => ({
        date: yearData.date,
        articles: yearData.articles,
      })) || [],

    numberOfArticlesPerQueryPerMonthThisYearData:
      dashboardData.numberOfArticlesPerQueryPerMonthThisYearData?.[selectedQueryIndex]?.map((monthData) => ({
        date: monthData.date,
        articles: monthData.articles,
      })) || [],

    numberOfArticlesPerQuery: selectedQueryIndex === 0
      ? [...dashboardData.numberOfArticlesPerQuery]
      : dashboardData.numberOfArticlesPerQuery.length > 0
        ? dashboardData.numberOfArticlesPerQuery.reduce((acc, queryData, index) => {
            if (index === selectedQueryIndex) {
              acc.push({ name: queryData.name, value: queryData.value });
            }
            return acc;
          }, [])
        : [],
    aiClassificationData: {
      yes: dashboardData.aiClassificationData.yes[selectedQueryIndex] || 0,
      no: dashboardData.aiClassificationData.no[selectedQueryIndex] || 0,
      maybe: dashboardData.aiClassificationData.maybe[selectedQueryIndex] || 0,
      notClassified: dashboardData.aiClassificationData.notClassified[selectedQueryIndex] || 0,
    },
    articlesWithInsights: dashboardData.articlesWithInsights[selectedQueryIndex] || 0,
    };
  }, [selectedQueryIndex, dataFetched, dashboardData]);

  useEffect(() => {
    setSelectedQuery(filterDataByQueryIndex);
  }, [filterDataByQueryIndex]);

  const handleQueryChange = (event) => {
    const index = event.target.value;
    setSelectedQueryIndex(index);
  };

  if (!dataFetched) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="calc(100vh - 80px)"
        className="animate-fade-in-up"
      >
        <div className="loading-spinner-modern"></div>
      </Box>
    );
  }

  // Calculate total articles
  const totalArticles = (selectedQuery?.totalReadUnreadData?.read || 0) + (selectedQuery?.totalReadUnreadData?.unread || 0);
  const readPercentage = totalArticles > 0 ? ((selectedQuery?.totalReadUnreadData?.read || 0) / totalArticles * 100).toFixed(1) : 0;
  
  // Calculate AI classification metrics
  const aiYes = selectedQuery?.aiClassificationData?.yes || 0;
  const aiNo = selectedQuery?.aiClassificationData?.no || 0;
  const aiMaybe = selectedQuery?.aiClassificationData?.maybe || 0;
  const aiNotClassified = selectedQuery?.aiClassificationData?.notClassified || 0;
  // eslint-disable-next-line no-unused-vars
  const aiTotal = aiYes + aiNo + aiMaybe + aiNotClassified;
  const aiClassified = aiYes + aiNo + aiMaybe;
  const aiAcceptanceRate = aiClassified > 0 ? ((aiYes / aiClassified) * 100).toFixed(1) : 0;
  const articlesWithInsights = selectedQuery?.articlesWithInsights || 0;
  const insightsPercentage = totalArticles > 0 ? ((articlesWithInsights / totalArticles) * 100).toFixed(1) : 0;

  return (
    <>
      <Box sx={{ 
        height: 'calc(100vh - 80px)', 
        padding: '2rem',
        background: 'var(--surface)',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--border-strong)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'var(--accent)',
        },
      }}>
        <div className="animate-fade-in-up">
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ 
              color: 'var(--text-primary)', 
              fontWeight: 700,
              mb: 0.5
            }}>
              Analytics Dashboard
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'var(--text-secondary)',
              fontSize: '0.95rem'
            }}>

              </Typography>
          </Box>

          {/* Query Selector - Full Width */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{
              background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
              border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '1.5rem',
            }}>
              <FormControl fullWidth>
                <InputLabel 
                  id="query-select-label" 
                  sx={{ 
                    color: 'var(--text-muted)',
                    '&.Mui-focused': {
                      color: '#6c3082'
                    }
                  }}
                >
                  Select Research Query
                </InputLabel>
                <Select
                  labelId="query-select-label"
                  id="query-select"
                  value={selectedQueryIndex}
                  label="Select Research Query"
                  onChange={handleQueryChange}
                  sx={{
                    color: 'var(--text-primary)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--border)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6c3082',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6c3082',
                    },
                    '& .MuiSelect-icon': {
                      color: 'var(--text-muted)',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        '& .MuiMenuItem-root': {
                          color: 'var(--text-primary)',
                          '&:hover': {
                            backgroundColor: 'rgba(108, 48, 130, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'var(--accent-gradient)',
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: 'var(--accent-gradient)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {dashboardData.queriesList.map((query, index) => (
                    <MenuItem key={index} value={index}>
                      {query.query}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* KPI Cards Section - Clean Flat Design */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Row 1: Primary Metrics */}
            {/* Total Articles */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.25rem',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                  borderColor: isDarkMode ? 'var(--border-strong)' : '#bdbdbd',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}>
                  Total Articles
                  </Typography>
                  <Article sx={{ color: 'var(--text-secondary)', fontSize: 20 }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  color: 'var(--text-primary)', 
                  fontWeight: 600,
                  fontSize: '2rem',
                  mb: 0.5
                }}>
                  {totalArticles}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Across all queries
                </Typography>
              </Box>
            </Grid>

            {/* Read Articles */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.25rem',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                  borderColor: isDarkMode ? 'var(--border-strong)' : '#bdbdbd',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}>
                    Read Articles
                  </Typography>
                  <CheckCircle sx={{ color: '#468230', fontSize: 20 }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  color: 'var(--text-primary)', 
                  fontWeight: 600,
                  fontSize: '2rem',
                  mb: 0.5
                }}>
                      {selectedQuery?.totalReadUnreadData?.read || 0}
                    </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {readPercentage}% completion rate
                </Typography>
              </Box>
            </Grid>

            {/* Unread Articles */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.25rem',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                  borderColor: isDarkMode ? 'var(--border-strong)' : '#bdbdbd',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}>
                    Unread Articles
                    </Typography>
                  <Schedule sx={{ color: '#ff9800', fontSize: 20 }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  color: 'var(--text-primary)', 
                  fontWeight: 600,
                  fontSize: '2rem',
                  mb: 0.5
                }}>
                      {selectedQuery?.totalReadUnreadData?.unread || 0}
                    </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Pending review
                </Typography>
              </Box>
            </Grid>

            {/* Active Queries */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.25rem',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                  borderColor: isDarkMode ? 'var(--border-strong)' : '#bdbdbd',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}>
                    Active Queries
                  </Typography>
                  <TrendingUp sx={{ color: '#6c3082', fontSize: 20 }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  color: 'var(--text-primary)', 
                  fontWeight: 600,
                  fontSize: '2rem',
                  mb: 0.5
                }}>
                  {Math.max(0, dashboardData.queriesList.length - 1)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Research topics
                </Typography>
              </Box>
            </Grid>

            {/* AI Classification Rate */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.25rem',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                  borderColor: isDarkMode ? 'var(--border-strong)' : '#bdbdbd',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}>
                    AI Acceptance Rate
                  </Typography>
                  <Psychology sx={{ color: '#9c27b0', fontSize: 20 }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  color: 'var(--text-primary)', 
                  fontWeight: 600,
                  fontSize: '2rem',
                  mb: 0.5
                }}>
                  {aiAcceptanceRate}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {aiYes} of {aiClassified} classified
                </Typography>
              </Box>
            </Grid>

            {/* AI Insights Coverage */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.25rem',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                  borderColor: isDarkMode ? 'var(--border-strong)' : '#bdbdbd',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}>
                    AI Insights Generated
                    </Typography>
                  <Lightbulb sx={{ color: '#ffa726', fontSize: 20 }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  color: 'var(--text-primary)', 
                  fontWeight: 600,
                  fontSize: '2rem',
                  mb: 0.5
                }}>
                  {articlesWithInsights}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {insightsPercentage}% coverage
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={2}>
            {/* Cumulative Read/Unread - Area Chart */}
            <Grid item xs={12} md={8}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.5rem',
                height: '400px',
              }}>
                <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  Cumulative Progress (This Year)
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <AreaChart data={selectedQuery?.cumulativeReadUnreadArticlesPerQueryPerMonthThisYearData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
                    <defs>
                      <linearGradient id="colorRead" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#468230" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#468230" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorUnread" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6c3082" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#6c3082" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                    <YAxis 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px', color: 'var(--text-secondary)' }}
                      iconType="circle"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="read" 
                      stroke="#468230" 
                      fillOpacity={1} 
                      fill="url(#colorRead)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="unread" 
                      stroke="#6c3082" 
                      fillOpacity={1} 
                      fill="url(#colorUnread)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            {/* AI Classification Distribution - Donut Chart */}
              <Grid item xs={12} md={4}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.5rem',
                height: '400px',
              }}>
                <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  AI Classification
                  </Typography>
                <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                      <Pie
                        data={[
                        { name: "Accepted", value: aiYes },
                        { name: "Maybe", value: aiMaybe },
                        { name: "Rejected", value: aiNo },
                        ]}
                        cx="50%"
                        cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        <Cell fill="#468230" />
                      <Cell fill="#ff9800" />
                      <Cell fill="#f44336" />
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
              </Box>
              </Grid>

            {/* Read/Unread Per Year - Stacked Bar */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.5rem',
                height: '350px',
              }}>
                <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  Articles by Year (Read/Unread)
                  </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={selectedQuery?.numberReadUnreadArticlesPerQueryPerYearData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                    <YAxis 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                      <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px', color: 'var(--text-secondary)' }}
                      iconType="circle"
                    />
                    <Bar dataKey="read" stackId="a" fill="#468230" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="unread" stackId="a" fill="#6c3082" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
              </Box>
              </Grid>

            {/* Articles Per Month - Bar Chart */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.5rem',
                height: '350px',
              }}>
                <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  Monthly Distribution (This Year)
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={selectedQuery?.numberOfArticlesPerQueryPerMonthThisYearData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                    <YAxis 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="articles" fill="#468230" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            {/* Articles Per Year - Line Chart */}
            <Grid item xs={12}>
              <Box sx={{
                background: isDarkMode ? 'var(--surface-2)' : '#ffffff',
                border: isDarkMode ? '1px solid var(--border)' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.5rem',
                height: '350px',
              }}>
                <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  Articles Over Time
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={selectedQuery?.numberOfArticlesPerQueryPerYearData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                    <YAxis 
                      tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} 
                      stroke={isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="articles" 
                      stroke="#6c3082" 
                      strokeWidth={3}
                      dot={{ fill: '#6c3082', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
        </Grid>
      </div>
      </Box>
    </>
  );
}

export default Dashboard;
